import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import StickyMobileCta from '../components/StickyMobileCta';
import {
  Stethoscope, Syringe, Shield, ClipboardCheck, FlaskConical, ScanLine, Bandage,
  Scissors, HeartPulse, Baby, Sparkles, Users, GraduationCap, Microscope, ClipboardList,
  MessageSquare, Repeat, Dog, Cat, MapPin, Phone, MessageCircle, CalendarDays, Car,
  ArrowRight, Home as HomeIcon, ShoppingBag, Bath, Bed, Wallet, Compass,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { CONTACT, GOOGLE_RATING } from '../data/mock';
import { trackCallClick, trackWhatsAppClick, trackBookButtonClick } from '../lib/analytics';

const TITLE = 'Veterinary Clinic Near Sector 93, Noida | Dog & Cat Vet | Ichi & Ori Pet Care';
const DESCRIPTION = 'Looking for a trusted veterinary clinic near Sector 93, Noida? Ichi & Ori Pet Care offers consultations, vaccinations, diagnostics, digital X-ray, surgery, spaying, neutering, and compassionate care for dogs and cats, just minutes from Sector 93.';
const CANONICAL = 'https://ichiandoripetcare.com/veterinary-clinic-sector-93-noida';
const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_petcare-preview-1/artifacts/w614bhhl_Waiting%20Area%202.jpg';
const MAP_EMBED = 'https://www.google.com/maps?q=Bhutani+Alphathum+Noida+World+One+Sector+90+Noida&output=embed';

const set = (sel, attr, val) => { const el = document.head.querySelector(sel); if (el) el.setAttribute(attr, val); };

const REASONS = [
  { Icon: GraduationCap, title: 'Experienced Veterinarians',     text: 'A small, focused team led by veterinarians who take time to understand every pet as an individual.' },
  { Icon: HeartPulse,    title: 'Compassionate, Evidence-Based', text: 'Diagnosis and treatment based on clinical evidence, always paired with gentle, unhurried handling.' },
  { Icon: MessageSquare, title: 'Thorough Consultations',        text: 'Real time to listen, examine and explain. You leave the room knowing what is happening and why.' },
  { Icon: Microscope,    title: 'Modern Diagnostics',            text: 'In-house CBC, ECG, BP monitoring and microscopy, so decisions can begin during the same visit.' },
  { Icon: ScanLine,      title: 'Digital X-Ray',                 text: 'On-site digital radiography reviewed on screen within minutes for fractures, chest and abdominal cases.' },
  { Icon: Scissors,      title: 'Surgical Expertise',            text: 'Planned procedures including spaying, neutering and soft tissue surgery with careful anaesthesia protocols.' },
  { Icon: ClipboardList, title: 'Transparent Treatment Plans',   text: 'Options, expected recovery and honest, upfront costs discussed before anything begins.' },
  { Icon: MapPin,        title: 'Minutes From Sector 93',       text: 'Located at Bhutani Alphathum (Noida World One), Sector 90, a short drive from Sector 93 and the Expressway.' },
];

const SERVICES = [
  { Icon: Stethoscope,    title: 'General Consultation',      text: 'Nose-to-tail examinations with time to listen and explain findings in plain language.', to: '/pet-doctor-noida' },
  { Icon: Syringe,        title: 'Vaccinations',              text: 'Core and lifestyle-based vaccinations for dogs and cats, guided by WSAVA schedules.', to: '/pet-vaccination-noida' },
  { Icon: Baby,           title: 'Puppy Vaccination',         text: 'A gentle first vaccination series, deworming plan and early wellness guidance.', to: '/puppy-vaccination-noida' },
  { Icon: Cat,            title: 'Kitten Vaccination',        text: 'Cat-friendly first visits, carrier training tips and a paced vaccination timeline.', to: '/kitten-vaccination-noida' },
  { Icon: FlaskConical,   title: 'Diagnostics',               text: 'CBC, biochemistry, ECG, BP, urine analysis and microscopy for faster, better-informed decisions.', to: '/pet-diagnostics-noida' },
  { Icon: ScanLine,       title: 'Digital X-Ray',             text: 'High-resolution digital radiography, safe for both dogs and cats, with quick on-screen review.', to: '/pet-x-ray-noida' },
  { Icon: Bandage,        title: 'Soft Tissue Surgery',       text: 'Wound repair, tumour removal, aural hematoma correction and other planned soft tissue procedures.', to: '/pet-surgery-noida' },
  { Icon: HeartPulse,     title: 'Spaying',                   text: 'Ovariohysterectomy for female dogs and cats, with pre-surgery workup and calm aftercare.', to: '/spaying-surgery-noida' },
  { Icon: Shield,         title: 'Neutering',                 text: 'Castration for male dogs and cats, with clear conversations about timing and recovery.', to: '/neutering-surgery-noida' },
  { Icon: ClipboardCheck, title: 'Senior Pet Care',           text: 'Screening for kidney, cardiac, joint and dental changes, with quality-of-life at the centre.', to: '/pet-doctor-noida' },
  { Icon: Sparkles,       title: 'Preventive Healthcare',     text: 'Deworming, tick and flea protection, dental checks and annual wellness planning.', to: '/veterinary-clinic-noida' },
];

const CONDITIONS = [
  { Icon: HeartPulse,     label: 'Vomiting' },
  { Icon: HeartPulse,     label: 'Diarrhoea' },
  { Icon: Sparkles,       label: 'Loss of appetite' },
  { Icon: Shield,         label: 'Tick fever' },
  { Icon: Sparkles,       label: 'Skin allergies' },
  { Icon: Stethoscope,    label: 'Ear infections' },
  { Icon: Bandage,        label: 'Limping' },
  { Icon: ScanLine,       label: 'Fractures' },
  { Icon: Stethoscope,    label: 'Respiratory illness' },
  { Icon: FlaskConical,   label: 'Urinary issues' },
  { Icon: ClipboardCheck, label: 'Routine wellness exams' },
];

const TRUST = [
  { Icon: GraduationCap, title: 'Experienced Veterinary Team', text: 'Care led by veterinarians who take the time to know your pet across visits, not just during emergencies.' },
  { Icon: Microscope,    title: 'Evidence-Based Care',         text: 'Decisions grounded in clinical findings and modern diagnostics, not guesswork or upselling.' },
  { Icon: ScanLine,      title: 'Modern Diagnostic Facilities',text: 'Digital X-Ray, in-house pathology and monitoring on-site, so most questions get answered the same day.' },
  { Icon: MessageSquare, title: 'Transparent Communication',   text: 'You will always know the diagnosis, the plan and the cost before we begin. No surprises, no jargon.' },
  { Icon: HeartPulse,    title: 'Compassionate Handling',      text: 'Slow, patient handling with breaks when a pet needs one. Comfort is part of the treatment.' },
  { Icon: Sparkles,      title: 'Clean, Premium Clinic',       text: 'A calm, thoughtfully designed space that keeps waiting flow low-stress for both pets and pet parents.' },
  { Icon: Users,         title: 'Excellent Google Reviews',    text: 'A steadily growing community of Noida pet parents who trust us with the pets they love the most.' },
];

const NEARBY = [
  'ATS Village',
  'Cleo County',
  'Amrapali Sapphire',
  'Lotus Panache',
  'Supertech Emerald Court area',
  'Sector 92',
  'Sector 94',
  'Sector 100',
  'Sector 108',
  'Sector 128',
];

const JOURNEY = [
  { Icon: HomeIcon,       title: 'Welcome & Registration', text: 'A warm welcome, a quick registration and a comfortable seat while your pet settles in.' },
  { Icon: MessageSquare,  title: 'Consultation',           text: 'We listen first. Your observations, the timeline of symptoms and any home context matter.' },
  { Icon: Stethoscope,    title: 'Physical Examination',   text: 'A patient, nose-to-tail exam that lets us gather what the numbers alone cannot show.' },
  { Icon: FlaskConical,   title: 'Diagnosis',              text: 'Where useful, in-house diagnostics run right away so treatment does not wait for another day.' },
  { Icon: ClipboardList,  title: 'Treatment Discussion',   text: 'Options, expected timelines, honest costs, and time for your questions before we begin.' },
  { Icon: Repeat,         title: 'Follow-up Care',         text: 'WhatsApp check-ins and easy access to us if anything feels off once your pet is home.' },
];

const UNDER_ONE_ROOF = [
  {
    Icon: Stethoscope,
    title: 'Veterinary Care',
    tag: 'Primary Focus',
    text: 'Consultations, vaccinations, diagnostics, digital X-ray, surgery and preventive healthcare for dogs and cats. This is the heart of what we do.',
    to: '/veterinary-clinic-noida',
    primary: true,
  },
  {
    Icon: Bath,
    title: 'Professional Grooming',
    text: 'Breed-appropriate bathing, brushing and finishing by trained hands, in a calm setting with familiar smells and faces.',
    to: '/grooming',
  },
  {
    Icon: Bed,
    title: 'Day & Overnight Boarding',
    text: 'Safe, supervised stays with regular walks and photo updates, from a few daytime hours to overnight care.',
    to: '/boarding',
  },
  {
    Icon: ShoppingBag,
    title: 'Pet Retail',
    text: 'A curated shelf of food, treats, supplements and accessories that we actually use for our own dogs and cats.',
    to: '/retail',
  },
];

const FAQS = [
  { q: 'Which veterinary clinic is near Sector 93?',
    a: 'Ichi & Ori Pet Care is located on the Ground Floor of Bhutani Alphathum (also known as Noida World One) in Sector 90, just a few minutes from Sector 93. We offer full-service veterinary care for dogs and cats and are easy to reach from Sector 93A, Sector 93B and nearby communities like ATS Village, Cleo County, Amrapali Sapphire and Lotus Panache.' },
  { q: 'Do you treat both dogs and cats?',
    a: 'Yes. Our team cares for dogs and cats of every age and life stage, from first vaccinations for puppies and kittens to gentle diagnostics and treatment for senior pets. Handling is tailored to your pet, not the other way around.' },
  { q: 'Do I need an appointment?',
    a: 'Yes, appointments help us give your pet undivided attention and keep waiting times short. You can book instantly on this page, message us on WhatsApp, or call the clinic. Urgent cases are always accommodated on priority whenever possible.' },
  { q: 'Do you offer Digital X-Ray?',
    a: 'Yes. We have a modern Digital DR X-Ray system on-site, with images reviewed on screen within minutes. It is useful for fractures, chest and abdominal cases, and pre-surgery assessments.' },
  { q: 'Do you perform surgeries?',
    a: 'Yes. We perform planned surgical procedures including spaying, neutering, soft tissue surgery, tumour removal and wound repair. Every surgery follows a pre-surgery health check and a clear conversation about recovery and cost.' },
  { q: 'Do you vaccinate puppies?',
    a: 'Yes. We provide the full puppy vaccination series along with deworming, tick and flea protection, nutrition guidance and gentle handling advice for first-time pet parents.' },
  { q: 'Do you provide grooming?',
    a: 'Yes, professional grooming is available alongside veterinary care. Many families combine a wellness check with a bath or a full grooming session in the same visit.' },
  { q: 'Do you provide boarding?',
    a: 'Yes. We offer safe day boarding and overnight boarding for dogs and cats, with regular walks, supervised care and photo updates through the day.' },
  { q: 'Is parking available?',
    a: 'Yes, parking is available within the Bhutani Alphathum (Noida World One) complex. Ground-floor access makes drop-off and pick-up easy for pets and pet parents alike.' },
  { q: 'How far is your clinic from Sector 93?',
    a: 'We are only a few minutes away by car. You will find us on the Ground Floor of Bhutani Alphathum, Sector 90, Noida, with direct access from the Noida-Greater Noida Expressway. It is a short, easy drive from Sector 93 residences and workplaces.' },
];

const INTERNAL_LINKS = [
  { to: '/veterinary-clinic-noida',   label: 'Veterinary Clinic Noida' },
  { to: '/dog-vet-noida',             label: 'Dog Vet Noida' },
  { to: '/cat-vet-noida',             label: 'Cat Vet Noida' },
  { to: '/pet-doctor-noida',          label: 'Pet Doctor Noida' },
  { to: '/pet-surgery-noida',         label: 'Pet Surgery' },
  { to: '/pet-diagnostics-noida',     label: 'Pet Diagnostics' },
  { to: '/pet-x-ray-noida',           label: 'Pet X-Ray' },
  { to: '/spaying-surgery-noida',     label: 'Spaying Surgery' },
  { to: '/neutering-surgery-noida',   label: 'Neutering Surgery' },
  { to: '/puppy-vaccination-noida',   label: 'Puppy Vaccination' },
  { to: '/kitten-vaccination-noida',  label: 'Kitten Vaccination' },
  { to: '/about',                     label: 'About Us' },
  { to: '/contact',                   label: 'Contact' },
  { to: '/book',                      label: 'Request Appointment' },
];

const VeterinaryClinicSector93Noida = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const orig = {
      d: document.head.querySelector('meta[name="description"]')?.getAttribute('content'),
      c: document.head.querySelector('link[rel="canonical"]')?.getAttribute('href'),
    };
    set('meta[name="description"]', 'content', DESCRIPTION);
    set('link[rel="canonical"]', 'href', CANONICAL);
    set('meta[property="og:title"]', 'content', TITLE);
    set('meta[property="og:description"]', 'content', DESCRIPTION);
    set('meta[property="og:url"]', 'content', CANONICAL);
    return () => {
      if (orig.d != null) set('meta[name="description"]', 'content', orig.d);
      if (orig.c != null) set('link[rel="canonical"]', 'href', orig.c);
    };
  }, []);

  const wa = `https://wa.me/${CONTACT.phoneRaw.replace(/\D/g, '')}?text=${encodeURIComponent("Hi, I'd like to book a veterinary consultation at Ichi & Ori Pet Care (Sector 93).")}`;

  const faqSchema = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: FAQS.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  };
  const vetSchema = {
    '@context': 'https://schema.org', '@type': 'VeterinaryCare',
    name: 'Ichi and Ori Pet Care, Veterinary Clinic Near Sector 93, Noida',
    url: CANONICAL,
    telephone: CONTACT.phoneRaw,
    priceRange: '\u20B9\u20B9',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Ground Floor, Bhutani Alphathum (Noida World One)',
      addressLocality: 'Sector 90, Noida',
      addressRegion: 'Uttar Pradesh',
      postalCode: '201305',
      addressCountry: 'IN',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: String(GOOGLE_RATING?.rating || 4.9),
      reviewCount: String(GOOGLE_RATING?.count || 107),
      bestRating: '5',
    },
    areaServed: [
      { '@type': 'Place', name: 'Sector 93, Noida' },
      { '@type': 'Place', name: 'Sector 92, Noida' },
      { '@type': 'Place', name: 'Sector 94, Noida' },
      { '@type': 'Place', name: 'Sector 100, Noida' },
      { '@type': 'Place', name: 'Sector 108, Noida' },
    ],
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://ichiandoripetcare.com/' },
      { '@type': 'ListItem', position: 2, name: 'Veterinary Clinic near Sector 93, Noida', item: CANONICAL },
    ],
  };

  return (
    <div className="bg-white" data-testid="vet-clinic-sector-93-page">
      <Helmet>
        <title>{TITLE}</title>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(vetSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* 1 — HERO */}
      <section className="relative bg-gradient-to-br from-[#1a0b3d] via-[#3b1d76] to-[#5b3eb5] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_30%_20%,#e870b6_0%,transparent_40%),radial-gradient(circle_at_80%_70%,#5b3eb5_0%,transparent_45%)]" />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 py-16 sm:py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-2 sm:py-1.5 rounded-full bg-[#e870b6]/20 border border-[#e870b6]/40 text-[#ffc1e0] text-[11px] sm:text-xs font-bold tracking-[0.18em] mb-6">
              <Stethoscope className="w-3.5 h-3.5 flex-shrink-0" />VETERINARY CLINIC · NEAR SECTOR 93, NOIDA
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.03] mb-6">
              Trusted Veterinary Clinic Near Sector 93, Noida
            </h1>
            <p className="text-base sm:text-lg text-white/85 leading-relaxed mb-4 max-w-xl">
              Looking for a trusted veterinary clinic near Sector 93, Noida? Welcome to Ichi &amp; Ori Pet Care, located on the Ground Floor of Bhutani Alphathum (Noida World One) in Sector 90, just a few minutes from Sector 93.
            </p>
            <p className="text-sm sm:text-base text-white/75 leading-relaxed mb-6 max-w-xl">
              Our experienced veterinarians provide compassionate, evidence-based care for dogs and cats, including consultations, vaccinations, diagnostics, digital X-ray, surgery, and preventive healthcare, all under one roof.
            </p>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/90 text-[13px] sm:text-sm font-medium mb-8">
              <MapPin className="w-4 h-4 text-[#ffc1e0] flex-shrink-0" />
              Ground Floor, Bhutani Alphathum · Minutes from Sector 93
            </div>

            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3 sm:gap-4">
              <Link to="/book" onClick={() => trackBookButtonClick('vet_sec93_hero')} className="w-full sm:w-auto">
                <Button data-testid="hero-book-btn" className="w-full sm:w-auto bg-[#e870b6] hover:bg-[#d85ba0] text-white rounded-full px-7 py-6 text-base font-semibold shadow-xl">
                  <CalendarDays className="w-5 h-5 mr-2" />Request Appointment
                </Button>
              </Link>
              <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('vet_sec93_hero')} className="w-full sm:w-auto">
                <Button data-testid="hero-wa-btn" className="w-full sm:w-auto bg-[#25d366] hover:bg-[#1eb858] text-white rounded-full px-7 py-6 text-base font-semibold shadow-xl">
                  <MessageCircle className="w-5 h-5 mr-2" />WhatsApp
                </Button>
              </a>
              <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('vet_sec93_hero')} className="w-full sm:w-auto sm:ml-1" data-testid="hero-call-link">
                <span className="inline-flex items-center gap-2 text-white/85 hover:text-white font-medium text-[14px] sm:text-[15px] underline decoration-white/30 underline-offset-4 hover:decoration-white transition-colors px-1 py-2">
                  <Phone className="w-4 h-4" /> Call Now
                </span>
              </a>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl border border-white/15 aspect-[4/5]">
              <img src={HERO_IMAGE} alt="Inside the Ichi and Ori Pet Care clinic, a short drive from Sector 93, Noida (waiting area and consultation rooms)" loading="eager" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* 2 — WHY PET PARENTS FROM SECTOR 93 CHOOSE ICHI & ORI */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">WHY SECTOR 93 CHOOSES ICHI &amp; ORI</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Care that feels close, careful and honest.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">
              Pet parents from Sector 93 and neighbouring Expressway communities visit us because the care feels different. Slower, clearer, and built around your pet as an individual.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {REASONS.map((r, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-[#f3d6f5]/60 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#fdf3fc] to-[#f3d6f5] flex items-center justify-center mb-5 flex-shrink-0">
                  <r.Icon className="w-5 h-5 text-[#5b3eb5]" />
                </div>
                <h3 className="font-serif text-[18px] font-bold text-[#1a0b3d] mb-3 leading-snug">{r.title}</h3>
                <p className="text-slate-600 text-[14.5px] leading-relaxed">{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 — VETERINARY SERVICES WE OFFER */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">VETERINARY SERVICES WE OFFER</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Everything your pet needs, close to Sector 93.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">
              From routine wellness to planned surgery, our services are designed for the everyday and the unexpected.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => (
              <Link key={i} to={s.to} data-testid={`service-card-${i}`} className="group bg-[#fdf7fb] rounded-2xl p-6 border border-[#f3d6f5]/60 hover:border-[#e870b6]/50 hover:shadow-md transition-all flex flex-col h-full">
                <div className="w-11 h-11 rounded-xl bg-white border border-[#f5d3e6] flex items-center justify-center mb-4 flex-shrink-0">
                  <s.Icon className="w-5 h-5 text-[#e870b6]" />
                </div>
                <h3 className="font-bold text-[#1a0b3d] text-[17px] mb-2 leading-snug">{s.title}</h3>
                <p className="text-[14.5px] text-[#3b1d76]/80 leading-relaxed flex-1">{s.text}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#5b3eb5] group-hover:text-[#e870b6] transition-colors">
                  Learn more <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 4 — COMMON CONDITIONS WE TREAT */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">COMMON CONDITIONS WE TREAT</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">The everyday concerns that bring pets to our clinic.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">
              If something feels off, it is almost always worth a call. Below are some of the concerns we see most often from Sector 93 and nearby families.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {CONDITIONS.map((c, i) => (
              <div key={i} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3.5 border border-[#f3d6f5]/60 shadow-sm">
                <c.Icon className="w-4 h-4 text-[#e870b6] flex-shrink-0" />
                <span className="text-[13px] sm:text-sm font-semibold text-[#1a0b3d] leading-snug">{c.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5 — WHY FAMILIES TRUST ICHI & ORI */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">WHY FAMILIES TRUST US</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Trust, built one visit at a time.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TRUST.map((t, i) => (
              <div key={i} className="bg-[#fdf7fb] rounded-2xl p-7 border border-[#f3d6f5]/60 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
                <div className="w-12 h-12 rounded-2xl bg-white border border-[#f5d3e6] flex items-center justify-center mb-5 flex-shrink-0">
                  <t.Icon className="w-5 h-5 text-[#5b3eb5]" />
                </div>
                <h3 className="font-serif text-[19px] font-bold text-[#1a0b3d] mb-3 leading-snug">{t.title}</h3>
                <p className="text-slate-600 text-[15px] leading-relaxed">{t.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6 — CONVENIENTLY LOCATED NEAR SECTOR 93 */}
      <section className="bg-gradient-to-br from-[#1a0b3d] via-[#3b1d76] to-[#5b3eb5] text-white py-20 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_15%_20%,#e870b6_0%,transparent_45%),radial-gradient(circle_at_85%_80%,#5b3eb5_0%,transparent_45%)]" />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#ffc1e0] mb-4">CONVENIENTLY LOCATED</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.08]">A short drive from Sector 93 and every nearby community.</h2>
            <p className="text-white/80 text-base sm:text-lg leading-relaxed mt-5">
              You will find us on the Ground Floor of Bhutani Alphathum (Noida World One) in Sector 90, just a few minutes from Sector 93. Ground-floor access makes drop-off and pick-up simple, even on a busy morning.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-12 max-w-3xl mx-auto">
            {NEARBY.map((n, i) => (
              <span key={i} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/95 text-[13px] sm:text-sm font-semibold">
                <MapPin className="w-3.5 h-3.5 text-[#ffc1e0]" /> {n}
              </span>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/15">
              <Compass className="w-6 h-6 text-[#ffc1e0] mb-3" />
              <h3 className="font-bold text-[16px] mb-1.5">Direct Expressway Access</h3>
              <p className="text-white/75 text-[14px] leading-relaxed">Straight off the Noida-Greater Noida Expressway, close to Sectors 93, 92, 94, 100 and 108.</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/15">
              <Car className="w-6 h-6 text-[#ffc1e0] mb-3" />
              <h3 className="font-bold text-[16px] mb-1.5">Parking Available</h3>
              <p className="text-white/75 text-[14px] leading-relaxed">Parking within Bhutani Alphathum, so you are never circling the block with a nervous pet.</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/15">
              <HomeIcon className="w-6 h-6 text-[#ffc1e0] mb-3" />
              <h3 className="font-bold text-[16px] mb-1.5">Easy Ground-Floor Access</h3>
              <p className="text-white/75 text-[14px] leading-relaxed">No stairs, no lifts, no fuss. A calm entry for pets who prefer things simple.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 7 — FIND US EASILY */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">FIND US EASILY</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Directions, at a glance.</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 items-stretch">
            <div className="rounded-3xl overflow-hidden shadow-lg border border-[#f3d6f5]/60 aspect-[4/3] lg:aspect-auto">
              <iframe
                title="Ichi and Ori Pet Care, Bhutani Alphathum (Noida World One), Sector 90, Noida (Map)"
                src={MAP_EMBED}
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: 380 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>

            <div className="flex flex-col justify-between gap-6">
              <div className="bg-[#fdf7fb] rounded-2xl p-6 border border-[#f3d6f5]/60">
                <div className="flex items-start gap-3 mb-4">
                  <MapPin className="w-5 h-5 text-[#e870b6] mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-bold text-[#1a0b3d] text-[17px] mb-1">Ichi &amp; Ori Pet Care</p>
                    <p className="text-[#3b1d76]/85 text-[15px] leading-relaxed">
                      Ground Floor, Bhutani Alphathum<br />
                      (Noida World One), Sector 90<br />
                      Noida, Uttar Pradesh 201305<br />
                      <span className="text-[#5b3eb5] font-semibold">Minutes from Sector 93</span>
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-5">
                  <div className="flex items-center gap-2 bg-white rounded-xl px-3 py-2.5 border border-[#f3d6f5]/60">
                    <Car className="w-4 h-4 text-[#e870b6]" />
                    <span className="text-[13px] font-semibold text-[#1a0b3d]">Parking Available</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white rounded-xl px-3 py-2.5 border border-[#f3d6f5]/60">
                    <Compass className="w-4 h-4 text-[#e870b6]" />
                    <span className="text-[13px] font-semibold text-[#1a0b3d]">Easy to Locate</span>
                  </div>
                </div>
              </div>

              <a
                href={`tel:${CONTACT.phoneRaw}`}
                onClick={() => trackCallClick('vet_sec93_directions')}
                data-testid="call-for-directions-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#1a0b3d] hover:bg-[#3b1d76] text-white text-[15px] font-semibold shadow-lg transition-colors"
              >
                <Phone className="w-4 h-4" /> Call for Directions
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 8 — WHAT TO EXPECT DURING YOUR VISIT */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">WHAT TO EXPECT</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">A visit that feels calm, from start to finish.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">
              Every visit follows a simple, honest rhythm. No jargon, no surprises, and never a rushed conversation.
            </p>
          </div>
          <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
            {JOURNEY.map((step, i) => (
              <li key={i} className="flex gap-4">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-11 h-11 rounded-full bg-white border border-[#f5d3e6] flex items-center justify-center">
                    <step.Icon className="w-5 h-5 text-[#e870b6]" />
                  </div>
                  <span className="text-[10px] tracking-[0.2em] font-bold text-[#5b3eb5] mt-2">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <div className="pt-1">
                  <h3 className="text-[#1a0b3d] font-bold text-base sm:text-lg mb-1.5 leading-snug">{step.title}</h3>
                  <p className="text-sm text-[#3b1d76]/80 leading-relaxed">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 9 — COMPLETE PET CARE UNDER ONE ROOF */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">COMPLETE PET CARE</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Veterinary care, with everything else close at hand.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">
              Veterinary care is the heart of what we do. Many families also appreciate being able to book a grooming, boarding or retail need alongside a routine visit, without adding another trip to the day.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {UNDER_ONE_ROOF.map((card, i) => (
              <Link
                key={i}
                to={card.to}
                data-testid={`under-one-roof-${i}`}
                className={`group relative rounded-2xl p-7 border transition-all duration-300 hover:-translate-y-1 flex flex-col h-full ${
                  card.primary
                    ? 'bg-gradient-to-br from-[#fdf3fc] to-[#f7dcf1] border-[#e870b6]/50 shadow-lg hover:shadow-xl'
                    : 'bg-[#fdf7fb] border-[#f3d6f5]/60 shadow-sm hover:shadow-md'
                }`}
              >
                {card.tag && (
                  <span className="absolute top-4 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#1a0b3d] text-white text-[10px] font-bold tracking-[0.14em]">
                    {card.tag}
                  </span>
                )}
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-5 flex-shrink-0 ${
                  card.primary
                    ? 'bg-white border border-[#e870b6]/40'
                    : 'bg-white border border-[#f5d3e6]'
                }`}>
                  <card.Icon className={`w-5 h-5 ${card.primary ? 'text-[#e870b6]' : 'text-[#5b3eb5]'}`} />
                </div>
                <h3 className="font-serif text-[19px] font-bold text-[#1a0b3d] mb-3 leading-snug">{card.title}</h3>
                <p className="text-slate-600 text-[14.5px] leading-relaxed flex-1">{card.text}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#5b3eb5] group-hover:text-[#e870b6] transition-colors">
                  Explore <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 10 — FAQ */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[880px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">FREQUENTLY ASKED</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Questions from Sector 93 pet parents, answered.</h2>
          </div>
          <Accordion type="single" collapsible defaultValue="faq-0" className="divide-y divide-[#f5d3e6] border-y border-[#f5d3e6] bg-white rounded-2xl px-4 sm:px-6" data-testid="sec93-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="px-1">
                <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left text-[#1a0b3d] font-semibold text-base sm:text-lg hover:no-underline py-5 sm:py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#3b1d76]/85 text-sm sm:text-base leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 10.5 — INTERNAL LINKS */}
      <section className="bg-white py-16 sm:py-20 border-y border-[#f3d6f5]/60">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-10 text-center">
          <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">EXPLORE RELATED CARE</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1a0b3d] leading-[1.1] mb-8">Related veterinary services and pages.</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {INTERNAL_LINKS.map((l, i) => (
              <Link key={i} to={l.to} data-testid={`internal-link-${i}`} className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#fdf7fb] border border-[#f3d6f5] hover:border-[#e870b6] hover:shadow-md text-[#1a0b3d] text-sm font-semibold transition-all">
                {l.label} <ArrowRight className="w-4 h-4 text-[#e870b6] group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 11 — FINAL CTA */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="rounded-[32px] bg-gradient-to-br from-[#5b3eb5] to-[#1a0b3d] text-white py-16 sm:py-20 px-6 relative overflow-hidden">
            <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_20%_30%,#e870b6_0%,transparent_45%)]" />
            <div className="relative text-center">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 leading-[1.08]">Your Pet Deserves Trusted Veterinary Care.</h2>
              <p className="text-white/85 mb-10 max-w-xl mx-auto text-base sm:text-lg leading-relaxed">
                Whether it is a routine checkup, a worrying symptom or a planned surgery, our team is here to help. Book a visit today and give your pet care that feels calm, honest and close to Sector 93.
              </p>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4">
                <Link to="/book" onClick={() => trackBookButtonClick('vet_sec93_final')} className="w-full sm:w-auto">
                  <Button data-testid="final-book-btn" className="w-full sm:w-auto bg-[#e870b6] hover:bg-[#d85ba0] rounded-full px-8 py-6 font-semibold shadow-xl">
                    <CalendarDays className="w-5 h-5 mr-2" />Request Appointment
                  </Button>
                </Link>
                <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('vet_sec93_final')} className="w-full sm:w-auto">
                  <Button data-testid="final-wa-btn" className="w-full sm:w-auto bg-[#25d366] hover:bg-[#1eb858] rounded-full px-8 py-6 font-semibold shadow-xl">
                    <MessageCircle className="w-5 h-5 mr-2" />WhatsApp
                  </Button>
                </a>
                <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('vet_sec93_final')} className="w-full sm:w-auto sm:ml-1" data-testid="final-call-link">
                  <span className="inline-flex items-center gap-2 text-white/85 hover:text-white font-medium text-[14px] sm:text-[15px] underline decoration-white/30 underline-offset-4 hover:decoration-white transition-colors px-1 py-2">
                    <Phone className="w-4 h-4" /> Call Now
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <StickyMobileCta source="vet_sec93" />
    </div>
  );
};

export default VeterinaryClinicSector93Noida;
